import { Link, NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useState } from "react";
import {
    LayoutDashboard,
    Users,
    BookOpen,
    CalendarDays,
    Settings,
    LogOut,
    Menu,
    Building2,
    GraduationCap,
    ChevronRight,
    Tickets,
    BarChart3,
    MessageSquareWarning,
    CalendarRange,
    Mail,
    Calculator,
    UserCheck,
    Newspaper,
    Headphones,
} from "lucide-react";
import { useAuth, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users },
    { to: "/admin/inscriptions", label: "Inscriptions", icon: Tickets },
    { to: "/admin/cursus", label: "Cursus", icon: BookOpen },
    { to: "/admin/formations", label: "Formations", icon: GraduationCap },
    { to: "/admin/sessions", label: "Sessions", icon: CalendarDays },
    { to: "/admin/planning", label: "Planning", icon: CalendarRange },
    { to: "/admin/salles", label: "Salles", icon: Building2 },
    { to: "/admin/comite-scientifique", label: "Comité scientifique", icon: UserCheck },
    { to: "/admin/actualites", label: "L'actualité PA", icon: Newspaper },
    { to: "/admin/qualiopi", label: "Qualiopi", icon: BarChart3 },
    { to: "/admin/previsionnel", label: "Prévisionnel", icon: Calculator },
    { to: "/admin/reclamations", label: "Réclamations", icon: MessageSquareWarning },
    { to: "/admin/demandes-conseiller", label: "Demandes conseiller", icon: Headphones },
    { to: "/admin/email-logs", label: "Emails", icon: Mail },
    { to: "/admin/parametres", label: "Paramètres", icon: Settings },
];

function crumbFor(pathname) {
    if (pathname === "/admin") return "Dashboard";
    if (pathname.startsWith("/admin/utilisateurs")) return "Utilisateurs";
    if (pathname.startsWith("/admin/inscriptions")) return "Inscriptions";
    if (pathname.startsWith("/admin/cursus")) return "Cursus";
    if (pathname.startsWith("/admin/formations")) return "Formations";
    if (pathname.startsWith("/admin/sessions")) return "Sessions";
    if (pathname.startsWith("/admin/salles")) return "Salles";
    if (pathname.startsWith("/admin/parametres")) return "Paramètres";
    if (pathname.startsWith("/admin/previsionnel")) return "Prévisionnel";
    if (pathname.startsWith("/admin/demandes-conseiller")) return "Demandes conseiller";
    return "Admin";
}

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        toast.success("Déconnecté");
        navigate("/login", { replace: true });
    };

    const Sidebar = (
        <aside className="flex h-full w-72 flex-col bg-brand-ink text-white">
            <div className="flex items-center gap-3 px-6 py-7 border-b border-white/10">
                <img src={LOGO_URL} alt="Cursus Physio" className="h-8 w-auto brightness-0 invert" />
            </div>
            <div className="px-6 py-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/40">
                Espace admin
            </div>
            <nav className="flex-1 space-y-1 px-4">
                {NAV.map((item) => {
                    const Icon = item.icon;
                    if (item.disabled) {
                        return (
                            <div
                                key={item.to}
                                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                                className="flex cursor-not-allowed items-center justify-between rounded-xl px-3 py-2.5 text-sm text-white/30"
                                onClick={() => toast.info("Bientôt disponible en Phase 4.")}
                                role="button"
                            >
                                <span className="flex items-center gap-3">
                                    <Icon className="h-4 w-4" /> {item.label}
                                </span>
                                <span className="text-[10px] uppercase tracking-wider text-white/25">Soon</span>
                            </div>
                        );
                    }
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={() => setOpen(false)}
                            data-testid={`admin-nav-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                    isActive
                                        ? "bg-brand-orange text-white shadow-[0_4px_16px_rgba(232,135,60,0.3)]"
                                        : "text-white/70 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <Icon className="h-4 w-4" /> {item.label}
                        </NavLink>
                    );
                })}
            </nav>

            <div className="border-t border-white/10 p-4">
                <div className="mb-3 rounded-xl bg-white/[0.03] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">Connecté</div>
                    <div className="mt-1 truncate text-sm font-medium">{user?.first_name} {user?.last_name}</div>
                    <div className="mt-0.5 truncate text-xs text-white/50">{user?.email}</div>
                </div>
                <button
                    onClick={handleLogout}
                    data-testid="admin-logout"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                >
                    <LogOut className="h-4 w-4" /> Déconnexion
                </button>
            </div>
        </aside>
    );

    return (
        <div className="flex min-h-screen bg-[#FAFAFA]" data-testid="admin-layout">
            <div className="hidden lg:flex">{Sidebar}</div>

            {/* Mobile drawer */}
            {open && (
                <div className="fixed inset-0 z-40 flex lg:hidden" data-testid="admin-mobile-drawer">
                    <div className="absolute inset-0 bg-brand-ink/50" onClick={() => setOpen(false)} />
                    <div className="relative h-full">{Sidebar}</div>
                </div>
            )}

            <div className="flex min-h-screen flex-1 flex-col">
                {/* Top header */}
                <header className="flex items-center gap-4 border-b border-brand-line bg-white px-6 py-4">
                    <button
                        className="lg:hidden"
                        onClick={() => setOpen(true)}
                        aria-label="Ouvrir le menu"
                        data-testid="admin-menu-toggle"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                    <div className="flex items-center gap-2 text-sm text-brand-smoke">
                        <Link to="/admin" className="hover:text-brand-ink">Admin</Link>
                        <ChevronRight className="h-3 w-3" />
                        <span className="font-medium text-brand-ink" data-testid="admin-breadcrumb">{crumbFor(location.pathname)}</span>
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        <div className="hidden text-right md:block">
                            <div className="text-sm font-medium text-brand-ink">{user?.first_name} {user?.last_name}</div>
                            <div className="text-xs text-brand-smoke capitalize">{user?.role}</div>
                        </div>
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-white font-display text-sm">
                            {(user?.first_name || "?")[0]?.toUpperCase()}
                        </div>
                    </div>
                </header>
                <main className="flex-1 overflow-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
